import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import Footer from '../components/Footer'

interface FashionSet {
    id: string;
    name: string;
    description: string;
    thumbnail: string;
    gender: 'male' | 'female';
}

type GenderFilter = 'all' | 'male' | 'female';

const FASHION_SET_IDS = ['james_bond', 'summer_dress', 'matthew', 'SJ', 'henry'];

// Minimal, elegant SVG icons for gender
const GenderIcon: React.FC<{ gender: 'male' | 'female' }> = ({ gender }) => {
    if (gender === 'male') {
        return (
            <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="10" cy="10" r="7.5" />
                <line x1="20" y1="20" x2="15.5" y2="15.5" />
            </svg>
        );
    }
    return (
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="10" r="7.5" />
            <line x1="12" y1="17.5" x2="12" y2="23" />
            <line x1="9" y1="20.5" x2="15" y2="20.5" />
        </svg>
    );
};

const ScrollButton: React.FC<{
    direction: 'left' | 'right';
    onClick: () => void;
    disabled: boolean;
}> = ({ direction, onClick, disabled }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`absolute ${direction === 'left' ? '-left-16' : '-right-16'} top-1/2 -translate-y-1/2
                   bg-white/90 backdrop-blur-sm rounded-xl p-6
                   transition-all duration-300 z-10 shadow-lg
                   ${disabled
                ? 'opacity-0 cursor-default pointer-events-none'
                : 'hover:bg-white hover:shadow-xl active:scale-95'
            }`}
        aria-label={`Scroll ${direction}`}
    >
        {direction === 'left' ? (
            <ChevronLeftIcon className="w-8 h-8 text-gray-800" />
        ) : (
            <ChevronRightIcon className="w-8 h-8 text-gray-800" />
        )}
    </button>
);

const LandingPage: React.FC = () => {
    const [fashionSets, setFashionSets] = useState<FashionSet[]>([])
    const [genderFilter, setGenderFilter] = useState<GenderFilter>('all')
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const [dragDistance, setDragDistance] = useState(0)
    const [isHovering, setIsHovering] = useState(false)
    const autoScrollIntervalRef = useRef<NodeJS.Timeout>()
    const [scrollDirection, setScrollDirection] = useState<'left' | 'right'>('right')

    const startAutoScroll = () => {
        if (autoScrollIntervalRef.current) {
            clearInterval(autoScrollIntervalRef.current);
        }

        autoScrollIntervalRef.current = setInterval(() => {
            const container = scrollContainerRef.current;
            if (container && !isHovering && !isDragging) {
                const maxScroll = container.scrollWidth - container.clientWidth;
                const scrollAmount = scrollDirection === 'right' ? 1 : -1;
                const newScrollLeft = container.scrollLeft + scrollAmount;

                if (newScrollLeft >= maxScroll) {
                    setScrollDirection('left');
                } else if (newScrollLeft <= 0) {
                    setScrollDirection('right');
                }

                container.scrollLeft = newScrollLeft;
            }
        }, 30);
    };

    useEffect(() => {
        startAutoScroll();
        return () => {
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
            }
        };
    }, [isHovering, isDragging, scrollDirection]);

    const checkScrollability = () => {
        const container = scrollContainerRef.current;
        if (container) {
            setCanScrollLeft(container.scrollLeft > 0)
            setCanScrollRight(
                container.scrollLeft < container.scrollWidth - container.clientWidth - 10
            )
        }
    }

    const scroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current;
        if (container) {
            const scrollAmount = 424; // Card width (400) + gap (24)
            const targetScroll = container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
            container.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    }

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true)
        setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0))
        setScrollLeft(scrollContainerRef.current?.scrollLeft || 0)
        setDragDistance(0)
    }

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return
        e.preventDefault()
        const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0)
        const walk = (x - startX) * 2
        setDragDistance(Math.abs(walk))
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollLeft - walk
        }
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    useEffect(() => {
        const loadFashionSets = async () => {
            try {
                const loadedSets = await Promise.all(
                    FASHION_SET_IDS.map(async (id) => {
                        try {
                            const response = await fetch(`/fashion_sets/${id}/src.txt`)
                            const text = await response.text()
                            const config = JSON.parse(text)

                            return {
                                id,
                                name: config.set_name,
                                description: config.description,
                                gender: config.gender,
                                thumbnail: `/fashion_sets/${id}/video.mp4`
                            }
                        } catch (error) {
                            console.error(`Error loading fashion set ${id}:`, error)
                            return null
                        }
                    })
                )

                setFashionSets(loadedSets.filter((set): set is FashionSet => set !== null))
            } catch (error) {
                console.error('Error loading fashion sets:', error)
            }
        }

        loadFashionSets()
    }, [])

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            checkScrollability();
            container.addEventListener('scroll', checkScrollability);
            window.addEventListener('resize', checkScrollability);

            return () => {
                container.removeEventListener('scroll', checkScrollability);
                window.removeEventListener('resize', checkScrollability);
            };
        }
    }, []);

    const filteredSets = fashionSets.filter(set =>
        genderFilter === 'all' || set.gender === genderFilter
    )

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <div className="flex-1 flex items-center">
                <div className="w-full">
                    <header className="mb-8 pt-12">
                        <div className="max-w-[1200px] mx-auto px-6">
                            <h1 className="text-center mb-2">
                                <span className="text-blue-600 font-medium text-[28px] tracking-tight">AI Stylist</span>
                                <span className="mx-3 text-gray-200 font-light">|</span>
                                <span className="text-gray-900 font-medium text-[28px] tracking-tight">Get This Look</span>
                            </h1>
                            <div className="text-sm text-gray-500 text-center mb-6">Find similar fashion items from your favorite looks</div>

                            <nav className="flex justify-center">
                                <div className="inline-flex items-center rounded-full bg-gray-50/80 backdrop-blur-sm p-1">
                                    <button
                                        onClick={() => setGenderFilter('all')}
                                        className={`px-5 py-1.5 rounded-full text-sm transition-all duration-200
                                            ${genderFilter === 'all'
                                                ? 'bg-white text-gray-900 shadow-sm font-medium'
                                                : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => setGenderFilter('male')}
                                        className={`px-5 py-1.5 rounded-full text-sm transition-all duration-200
                                            ${genderFilter === 'male'
                                                ? 'bg-white text-gray-900 shadow-sm font-medium'
                                                : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        Men
                                    </button>
                                    <button
                                        onClick={() => setGenderFilter('female')}
                                        className={`px-5 py-1.5 rounded-full text-sm transition-all duration-200
                                            ${genderFilter === 'female'
                                                ? 'bg-white text-gray-900 shadow-sm font-medium'
                                                : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        Women
                                    </button>
                                </div>
                            </nav>
                        </div>
                    </header>

                    <div className="relative"
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                    >
                        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-[1] pointer-events-none" />
                        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-[1] pointer-events-none" />

                        <div className="max-w-[1200px] relative mx-auto px-2">
                            <ScrollButton
                                direction="left"
                                onClick={() => scroll('left')}
                                disabled={!canScrollLeft}
                            />
                            <ScrollButton
                                direction="right"
                                onClick={() => scroll('right')}
                                disabled={!canScrollRight}
                            />

                            <div
                                ref={scrollContainerRef}
                                className="flex gap-4 overflow-x-auto overflow-y-hidden scroll-smooth cursor-grab active:cursor-grabbing"
                                style={{
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    WebkitOverflowScrolling: 'touch'
                                }}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                            >
                                {filteredSets.map((set) => (
                                    <Link
                                        key={set.id}
                                        to={`/style/${set.id}`}
                                        className="flex-none w-[290px] bg-white overflow-hidden 
                                                 hover:opacity-90 transition-opacity duration-300 
                                                 flex flex-col group cursor-pointer"
                                        onClick={(e) => {
                                            if (dragDistance > 5) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        <div className="relative pt-[133%]">
                                            <video
                                                className="absolute inset-0 w-full h-full object-cover"
                                                src={set.thumbnail}
                                                muted
                                                loop
                                                autoPlay
                                                playsInline
                                            />
                                        </div>
                                        <div className="py-4">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h2 className="text-base font-medium text-gray-900">{set.name}</h2>
                                                <GenderIcon gender={set.gender} />
                                            </div>
                                            <p className="text-sm text-gray-600 line-clamp-2">{set.description}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    )
}

export default LandingPage
